import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { money } from "../../lib/format";

const QUICK_CASH = [10000, 20000, 50000, 100000];

export default function PaymentModal({
  total,
  paymentMethods,
  onClose,
  onPay,
  busy,
  mode = "PAYMENT",
}) {
  const { t } = useTranslation();
  const isAddOrder = mode === "ADD_ORDER";
  const isCloseBill = mode === "CLOSE_BILL";

  const cashMethods = paymentMethods.filter((m) => m.type === "CASH");

  const [methodId, setMethodId] = useState(
    cashMethods[0]?.id || paymentMethods[0]?.id || ""
  );

  const [paid, setPaid] = useState("");
  const [complimentReason, setComplimentReason] = useState("");
  const [customerName, setCustomerName] = useState("");

  const method = paymentMethods.find((m) => m.id === methodId);
  const isCash = method?.type === "CASH";
  const isCompliment = method?.type === "COMPLIMENT";

  const paidNum = parseInt(paid || "0", 10) || 0;

  const change = useMemo(
    () => (isCash ? paidNum - total : 0),
    [isCash, paidNum, total]
  );

  const hasCustomerName = customerName.trim().length > 0;

  const canPay =
    (isCloseBill || hasCustomerName) &&
    methodId &&
    (!isCash || paidNum >= total) &&
    (!isCompliment || complimentReason.trim().length > 0);

  const canOpenBill = hasCustomerName && !busy;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      if (e.key !== "Enter" || busy) return;

      if (isAddOrder) {
        e.preventDefault();
        onPay({
          add_order: true,
        });
        return;
      }

        if (isCloseBill && canPay) {
          e.preventDefault();
          onPay({
            close_bill: true,
            payment: {
              method_id: methodId,
              amount_paid: isCash ? paidNum : total,
              ...(isCompliment
                ? { compliment_reason: complimentReason.trim() }
                : {}),
            },
          });
          return;
        }

      if (canPay) {
        e.preventDefault();
        onPay({
          customer_name: customerName.trim(),
          open_bill: false,
          payment: {
            method_id: methodId,
            amount_paid: isCash ? paidNum : total,
            ...(isCompliment
              ? { compliment_reason: complimentReason.trim() }
              : {}),
          },
        });
      }
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [
    isAddOrder,
    isCloseBill,
    canPay,
    busy,
    methodId,
    isCash,
    isCompliment,
    paidNum,
    total,
    complimentReason,
    customerName,
    onPay,
    onClose,
  ]);

  const handleOpenBill = () => {
    if (!canOpenBill) return;

    onPay({
      customer_name: customerName.trim(),
      open_bill: true,
      payment: null,
    });
  };

  const handleAddOrder = () => {
    if (busy) return;

    onPay({
      add_order: true,
    });
  };

  const handleCloseBill = () => {
    if (!canPay || busy) return;

    onPay({
      close_bill: true,
      payment: {
        method_id: methodId,
        amount_paid: isCash ? paidNum : total,
        ...(isCompliment
          ? { compliment_reason: complimentReason.trim() }
          : {}),
      },
    });
  };

  const handlePayment = () => {
    if (!canPay || busy) return;

    onPay({
      customer_name: customerName.trim(),
      open_bill: false,
      payment: {
        method_id: methodId,
        amount_paid: isCash ? paidNum : total,
        ...(isCompliment
          ? { compliment_reason: complimentReason.trim() }
          : {}),
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border w-full max-w-3xl rounded-2xl p-6 shadow-2xl space-y-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="payment-modal"
      >
        <div className="flex justify-between items-center">
          <h3 className="font-heading text-xl font-bold">{t("Payment")}</h3>

          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="payment-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="text-center py-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("Amount Due")}
          </p>

          <p
            className="font-mono text-4xl font-extrabold text-primary"
            data-testid="payment-amount-due"
          >
            {money(total)}
          </p>
        </div>

          {!isAddOrder && (
            <>
          {!isCloseBill && (
            <div className="space-y-2">
              {/* CUSTOMER NAME */}
              <label className="text-sm font-semibold">
                {t("Customer Name")} <span className="text-red-400">*</span>
              </label>

              <input
                data-testid="payment-customer-name"
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder={t("Customer name placeholder")}
                maxLength={150}
                className="w-full h-12 px-4 rounded-xl bg-secondary border border-border focus:border-primary focus:outline-none text-sm"
                autoFocus
              />

              <p className="text-xs text-muted-foreground">
                {t("Customer name required")}
              </p>
            </div>
          )}

          {/* PAYMENT METHODS */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {paymentMethods.map((m) => (
              <button
                key={m.id}
                data-testid={`payment-method-${m.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                onClick={() => setMethodId(m.id)}
                className={`h-14 rounded-xl border font-semibold text-sm transition active:scale-95 ${
                  methodId === m.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-secondary border-border hover:bg-accent"
                }`}
              >
                {m.name}
              </button>
            ))}
          </div>

          {/* CASH */}
          {isCash && (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                {QUICK_CASH.map((v) => (
                  <button
                    key={v}
                    data-testid={`quick-cash-${v / 1000}k`}
                    onClick={() => setPaid(String(v))}
                    className="px-4 py-2 rounded-lg bg-secondary border border-border text-sm font-mono font-bold hover:bg-accent active:scale-95"
                  >
                    {money(v)}
                  </button>
                ))}

                <button
                  data-testid="quick-cash-exact"
                  onClick={() => setPaid(String(total))}
                  className="px-4 py-2 rounded-lg bg-primary/15 border border-primary/40 text-primary text-sm font-bold hover:bg-primary/25 active:scale-95"
                >
                  {t("Exact")}
                </button>
              </div>

              <input
                data-testid="payment-cash-input"
                type="number"
                inputMode="numeric"
                value={paid}
                onChange={(e) => setPaid(e.target.value)}
                placeholder={t("Cash received")}
                className="w-full h-14 px-4 rounded-xl bg-secondary border border-border focus:border-primary focus:outline-none font-mono text-2xl font-bold text-center"
              />

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("Change")}</span>

                <span
                  className={`font-mono text-lg font-bold ${
                    change >= 0 ? "text-emerald-400" : "text-red-400"
                  }`}
                  data-testid="payment-change"
                >
                  {paid === "" ? money(0) : money(Math.max(change, 0))}
                </span>
              </div>
            </div>
          )}

          {/* COMPLIMENT */}
          {isCompliment && (
            <div className="space-y-2">
              <label className="text-sm font-semibold">
                {t("Complimentary Reason")}{" "}
                <span className="text-red-400">*</span>
              </label>

              <textarea
                data-testid="payment-compliment-reason"
                value={complimentReason}
                onChange={(e) => setComplimentReason(e.target.value)}
                placeholder={t("Complimentary reason example")}
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-primary focus:outline-none text-sm resize-none"
              />

              <p className="text-xs text-muted-foreground">
                {t("Complimentary required")}
              </p>
            </div>
          )}
            </>
          )}

          {/* ACTIONS */}
          {isAddOrder ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={onClose}
                data-testid="add-order-cancel-button"
                className="h-14 rounded-xl border border-border bg-secondary font-extrabold text-base hover:bg-secondary/80 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("CANCEL")}
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={handleAddOrder}
                data-testid="add-order-confirm-button"
                className="h-14 rounded-xl bg-primary text-primary-foreground font-extrabold text-base hover:opacity-90 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? t("Processing…") : t("ADD TO BILL")}
              </button>
            </div>
            ) : isCloseBill ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={onClose}
                data-testid="close-bill-cancel-button"
                className="h-14 rounded-xl border border-border bg-secondary font-extrabold text-base hover:bg-secondary/80 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("CANCEL")}
              </button>

              <button
                type="button"
                disabled={!canPay || busy}
                onClick={handleCloseBill}
                data-testid="close-bill-confirm-button"
                className="h-14 rounded-xl bg-primary text-primary-foreground font-extrabold text-base hover:opacity-90 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? t("Processing…") : `${t("CLOSE BILL")} · ${money(total)}`}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <button
                  disabled={!canOpenBill || busy}
                  onClick={handleOpenBill}
                  data-testid="payment-open-bill-button"
                  className="h-14 rounded-xl border border-primary/50 bg-primary/10 text-primary font-extrabold text-base hover:bg-primary/20 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("OPEN BILL")}
                </button>

                <button
                  disabled={!canPay || busy}
                  onClick={handlePayment}
                  data-testid="payment-confirm-button"
                  className="h-14 rounded-xl bg-primary text-primary-foreground font-extrabold text-base hover:opacity-90 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? t("Processing…") : `${t("PAYMENT")} · ${money(total)}`}
                </button>
              </div>

              <p className="text-[11px] text-muted-foreground text-center">
                {t("Open bill description")}
              </p>
            </>
          )}
      </div>
    </div>
  );
}
